import EmbedController from "../../embed/EmbedController";
import AlertSlackOfError from "../../monitoring/AlertSlackOfError";
import { delay } from "./delay";
import { convertToUSD } from "./getFinalPrice";
import { uploadImageToDigitalOcean } from "./uploadImageToDigitalOcean";

function generateProductTitle(productObj) {
  return `Custom Seat Cover: ${productObj.displayName}`;
}

function generateProductHtml(tt_product_properties) {
  let htmlString = ``;
  for (const [key, value] of Object.entries(tt_product_properties)) {
    htmlString += `<p><b>${key}:</b> ${value}</p>`;
  }
  return htmlString;
}

function generateProductBody(productObj, cartSubmissionObj, finalPrice) {
  const tags = ["tastetest3d-order", "tastetest3d-seat"];
  if (cartSubmissionObj.items[0].properties["Top Color"].toLowerCase().includes("taka")) tags.push("cab-taka-pattern");
  if (cartSubmissionObj.items[0].properties["Top Color"].toLowerCase().includes("khalifa kush")) tags.push("khalifa-kush-pattern");
  const propertyValues = Object.values(cartSubmissionObj.items[0].properties);
  if (propertyValues.some((val) => typeof val === "string" && val.toLowerCase().includes("gator"))) {
    tags.push("black-gator-pattern");
  }
  return {
    product: {
      title: generateProductTitle(productObj),
      body_html: generateProductHtml(cartSubmissionObj.items[0].properties),
      vendor: "Thrill Seekers",
      product_type: "tastetest3d-order",
      template_suffix: "3d-customized",
      tags: tags.concat(productObj.tags),
      variants: [
        {
          price: convertToUSD(finalPrice),
          weight: productObj.weight,
          inventory_management: null,
        },
      ],
    },
  };
}

async function saveImageViaEmail(attachmentBase64, sessionId) {
  try {
    const url = `https://${window.location.hostname}/.netlify/functions/send-email`;
    const res = await fetch(url, {
      method: "POST",
      body: JSON.stringify({
        project: "Thrill Seekers",
        message: "Saving Uploaded Logo",
        attachmentBase64,
      }),
    });
    const data = await res.json();
    return data;
  } catch (err) {
    console.log("ERROR createUniqueShopifyProduct.js saveImageViaEmail ", err.message);
    AlertSlackOfError("createUniqueShopifyProduct.js saveImageViaEmail", `error: ${err.message}`, sessionId);
    return false;
  }
}

export function shopifyProductUrl(handle) {
  const fallback = "https://thrillseekers.com";
  let origin = fallback;
  try {
    origin = new URL(EmbedController.parentUrl || fallback).origin;
  } catch (e) {
    origin = fallback;
  }
  return origin + "/products/" + handle;
}

export async function applyCustomLogoUpload(cartSubmissionObj, sessionId, { itemsState, setItemsActiveIds } = {}) {
  if (!cartSubmissionObj.items[0].properties["Custom Image URL"] || !cartSubmissionObj.tempCustomUploadImageInputs) {
    return false;
  }

  let DOUploadImageUrl = await uploadImageToDigitalOcean(cartSubmissionObj.items[0].properties["Custom Image URL"], "customImageUpload");
  if (DOUploadImageUrl.slice(-11) === "default.jpg") {
    await delay(2000);
    DOUploadImageUrl = await uploadImageToDigitalOcean(cartSubmissionObj.items[0].properties["Custom Image URL"], "customImageUpload");
    if (DOUploadImageUrl.slice(-11) === "default.jpg") {
      await saveImageViaEmail(cartSubmissionObj.items[0].properties["Custom Image URL"], sessionId);
    }
  }

  cartSubmissionObj.items[0].properties["Custom Image URL"] = DOUploadImageUrl;
  cartSubmissionObj.tempCustomUploadImageInputs.inputs.uploaded_logo_src = DOUploadImageUrl;

  // Update items/hash with the hosted logo URL before reading _customUrl
  if (setItemsActiveIds && itemsState) {
    setItemsActiveIds({
      ...itemsState.activeIds,
      [cartSubmissionObj.customPatchFront]: cartSubmissionObj.tempCustomUploadImageInputs,
    });
  }

  delete cartSubmissionObj.customPatchFront;
  delete cartSubmissionObj.tempCustomUploadImageInputs;

  await delay(1000);
  return true;
}

async function createShopifyProduct(productObj, cartSubmissionObj, finalPrice, sessionId) {
  try {
    const productBody = generateProductBody(productObj, cartSubmissionObj, finalPrice);
    const url = `https://${window.location.hostname}/.netlify/functions/create-shopify-product`;
    const response = await EmbedController.sendShopifyProduct(url, productBody);
    return response;
  } catch (err) {
    console.log("ERROR createUniqueShopifyProduct.js createShopifyProduct ", err.message);
    AlertSlackOfError("createUniqueShopifyProduct.js createShopifyProduct", `error: ${err.message}`, sessionId);
    throw err;
  }
}

async function addProductToCollection(shopifyProductId, sessionId, retryCount = 0) {
  if (!EmbedController.isEmbedded) return false;

  try {
    const url = `https://${window.location.hostname}/.netlify/functions/add-product-to-collection`;
    await EmbedController.addProductToCollection(url, {
      product_id: shopifyProductId,
    });
    console.log(`Successfully added product ${shopifyProductId} to collection`);
    return true;
  } catch (err) {
    console.error("ERROR createUniqueShopifyProduct.js addProductToCollection", err.message);

    if (retryCount === 0) {
      console.log("Retrying collection assignment after 1 second...");
      await delay(1000);
      return addProductToCollection(shopifyProductId, sessionId, 1);
    }

    console.error("Collection assignment failed after retry, continuing with product creation");
    AlertSlackOfError(
      "createUniqueShopifyProduct.js addProductToCollection",
      `Failed to add product ${shopifyProductId} to collection after retry. Error: ${err.message}`,
      sessionId,
    );

    return false;
  }
}

async function createProductImage(shopifyProductId, imageSrc, sessionId) {
  const currentDate = new Date();
  const day = currentDate.getDate();
  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();
  const formattedDate = `${day}-${month}-${year}`;

  if (!EmbedController.isEmbedded) return false;
  try {
    const url = `https://${window.location.hostname}/.netlify/functions/upload-shopify-image`;
    await EmbedController.sendUploadProductImage(url, {
      product_id: shopifyProductId,
      image: {
        attachment: imageSrc,
        filename: `${formattedDate}-${shopifyProductId}.png`,
      },
    });
    return true;
  } catch (err) {
    console.error("ERROR createUniqueShopifyProduct.js createProductImage ", err.message);
    AlertSlackOfError(
      "createUniqueShopifyProduct.js createProductImage",
      `error: ${err.message}, productId: ${shopifyProductId}, imageSrc.length: ${imageSrc?.length}, imageSrc.slice(0, 100): ${imageSrc?.slice(0, 100)}`,
      sessionId,
    );
    throw err;
  }
}

export async function createUniqueShopifyProduct({ productObj, cartSubmissionObj, finalPrice, customImageBase64, sessionId }) {
  const { product } = await createShopifyProduct(productObj, cartSubmissionObj, finalPrice, sessionId);
  await addProductToCollection(product.id, sessionId);
  if (customImageBase64) {
    const imageRes = await createProductImage(product.id, customImageBase64, sessionId);
    if (!imageRes) await createProductImage(product.id, customImageBase64, sessionId);
  }
  return {
    productId: product.id,
    variantId: product.variants[0].id,
    handle: product.handle,
    url: shopifyProductUrl(product.handle),
  };
}
